import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Body, Button, Row, Stack } from '@leanin/ui';

import {
  useCreateBroadcast,
  type CreateBroadcastInput,
} from '@/lib/mutations/admin';
import {
  useCreateGroupBroadcast,
  type CreateGroupBroadcastInput,
} from '@/lib/mutations/groups';
import { useStepUp } from '@/lib/use-step-up';
import type { BroadcastDoc } from '@/lib/queries/broadcasts';
import { emptyBroadcastDoc } from './BroadcastBodyEditor';
import {
  BroadcastEditorFields,
  resolveAudience,
  type AudienceState,
  type BroadcastAudienceContext,
} from './BroadcastEditorFields';
import { ScheduleSendButton } from './ScheduleSendButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

export interface BroadcastComposerFormProps {
  readonly audience: BroadcastAudienceContext;
}

const EMPTY_AUDIENCE: AudienceState = {
  kind: 'all',
  circles: [],
  networks: [],
  members: [],
  recentDays: 30,
};

// The composer's three intents. `scheduledIso` rides on 'schedule' alone so a
// schedule can't be dispatched without a time and a send/draft can't carry one.
type ComposerAction =
  | { readonly mode: 'send' | 'draft' }
  | { readonly mode: 'schedule'; readonly scheduledIso: string };

export function BroadcastComposerForm({ audience }: BroadcastComposerFormProps) {
  const navigate = useNavigate();
  const createPlatform = useCreateBroadcast();
  const createGroup = useCreateGroupBroadcast();
  const { runStepUp } = useStepUp();

  const [subject, setSubject] = useState('');
  const [preheaderText, setPreheaderText] = useState('');
  const [bodyDoc, setBodyDoc] = useState<BroadcastDoc>(emptyBroadcastDoc());
  const [audienceState, setAudienceState] =
    useState<AudienceState>(EMPTY_AUDIENCE);
  const [replyTo, setReplyTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [savePending, setSavePending] = useState(false);

  const pending = createPlatform.isPending || createGroup.isPending;

  const dirty =
    subject.trim().length > 0 ||
    preheaderText.trim().length > 0 ||
    replyTo.trim().length > 0 ||
    !docIsEmpty(bodyDoc);

  const guard = useUnsavedChangesGuard({ dirty });

  const minimumFieldsForSave =
    subject.trim().length > 0 && !docIsEmpty(bodyDoc);

  // Returns true when the action completed, false when the user dismissed
  // the step-up reverification prompt. Validation and hard failures throw.
  const submit = async (action: ComposerAction): Promise<boolean> => {
    setError(null);
    if (subject.trim().length === 0) {
      throw new Error('Subject is required.');
    }
    if (docIsEmpty(bodyDoc)) {
      throw new Error('Message is required.');
    }

    const replyToValue = replyTo.trim().length > 0 ? replyTo.trim() : undefined;
    const preheaderValue =
      preheaderText.trim().length > 0 ? preheaderText.trim() : undefined;
    const optional = {
      ...(preheaderValue !== undefined
        ? { preheaderText: preheaderValue }
        : {}),
      ...(replyToValue !== undefined ? { replyTo: replyToValue } : {}),
    };

    if (audience.mode === 'platform') {
      const { audienceSpec } = resolveAudience(audienceState);
      const fields = {
        audienceSpec,
        sendVia: 'email' as const,
        subject: subject.trim(),
        bodyDoc,
        ...optional,
      };
      const input: CreateBroadcastInput =
        action.mode === 'schedule'
          ? { ...fields, mode: 'schedule', scheduledSendAt: action.scheduledIso }
          : { ...fields, mode: action.mode };
      // Committing a platform-wide send is step-up'd server-side (RBAC
      // Decision C). runStepUp opens Clerk's reverification modal on
      // `admin/requires_step_up` and retries once on success; it passes
      // through transparently for draft saves, which carry no step-up.
      const outcome = await runStepUp(
        () => createPlatform.mutateAsync(input),
        'Failed to send broadcast.',
      );
      if (outcome.status === 'cancelled') return false;
      if (outcome.status === 'error') throw new Error(outcome.message);
      return true;
    }

    const fields = {
      kind: audience.kind,
      groupId: audience.groupId,
      subject: subject.trim(),
      bodyDoc,
      ...optional,
    };
    const input: CreateGroupBroadcastInput =
      action.mode === 'schedule'
        ? { ...fields, mode: 'schedule', scheduledSendAt: action.scheduledIso }
        : { ...fields, mode: action.mode };
    await createGroup.mutateAsync(input);
    return true;
  };

  const navigateAway = () => {
    guard.allowNextNavigation();
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  const handleSendNow = async () => {
    try {
      if (await submit({ mode: 'send' })) navigateAway();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send.');
    }
  };

  const handleSchedule = async (iso: string) => {
    try {
      if (await submit({ mode: 'schedule', scheduledIso: iso })) navigateAway();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule.');
    }
  };

  const handleGuardSave = async () => {
    setDialogError(null);
    setSavePending(true);
    try {
      const completed = await submit({ mode: 'draft' });
      setSavePending(false);
      if (completed) guard.proceed();
    } catch (err) {
      setSavePending(false);
      setDialogError(err instanceof Error ? err.message : 'Failed to save.');
    }
  };

  const handleCancel = () => {
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  return (
    <Stack gap="lg">
      <BroadcastEditorFields
        audience={audience}
        subject={subject}
        onSubjectChange={setSubject}
        preheaderText={preheaderText}
        onPreheaderChange={setPreheaderText}
        bodyDoc={bodyDoc}
        onBodyDocChange={setBodyDoc}
        audienceState={audienceState}
        onAudienceStateChange={setAudienceState}
        replyTo={replyTo}
        onReplyToChange={setReplyTo}
        disabled={pending}
      />

      {error !== null ? (
        <Body size="sm" color="destructive">
          {error}
        </Body>
      ) : null}

      <Row gap="md" justify="flex-end" align="center">
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <ScheduleSendButton
          disabled={!minimumFieldsForSave}
          pending={pending}
          onSendNow={handleSendNow}
          onSchedule={handleSchedule}
        />
      </Row>

      <UnsavedChangesDialog
        open={guard.dialogOpen}
        onCancel={guard.reset}
        onSave={() => void handleGuardSave()}
        onDiscard={guard.proceed}
        saveDisabled={!minimumFieldsForSave}
        saveDisabledReason={
          !minimumFieldsForSave
            ? 'Subject and message are required to save as draft.'
            : undefined
        }
        savePending={savePending}
        error={dialogError}
      />
    </Stack>
  );
}

function docIsEmpty(doc: BroadcastDoc): boolean {
  const blocks = doc.content as ReadonlyArray<{ content?: ReadonlyArray<unknown> }>;
  if (blocks.length === 0) return true;
  return blocks.every((block) => {
    const inline = block.content;
    return inline === undefined || inline.length === 0;
  });
}

